import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  CircularProgress,
  Chip,
  FormControlLabel,
  Checkbox,
  ButtonGroup
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  GetApp as ExportIcon,
  Refresh as RefreshIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { useAuth } from '../../../../auth/useAuth';
import { useHedgeFundData } from '../../contexts/HedgeFundDataContext';
import { useTableSort } from '../../../../hooks/useTableSort';
import SortableTableHeader from '../../../../components/SortableTableHeader/SortableTableHeader';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Complete interface matching backend MonitorQueryView
interface MonitorDataRow {
  MonitorDataID: number;
  BackstopID: number;
  FieldID: number;
  MonitorDate: string;
  FieldValue: number;
  Note?: string;
  FundName: string;
  FieldName: string;
  DateDisp?: string;
  SoftLower?: number;
  HardLower?: number;
  SoftUpper?: number;
  HardUpper?: number;
  SoftViolation: boolean;
  HardViolation: boolean;
  RiskIndicatorName?: string;
  RiskLevel?: number;
  Invested?: boolean;
  LiveThreshold?: boolean;
  NumValues?: number;
  Timestamp?: string;
  UserName?: string;
}

interface MonitorField {
  FieldID: number;
  FieldName: string;
  RiskIndicatorID: number;
}

interface BrowseEditViewProps {
  onDataChange?: () => void;
}

const BrowseEditView: React.FC<BrowseEditViewProps> = ({ onDataChange }) => {
  const { getAccessToken } = useAuth();
  const { funds } = useHedgeFundData();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [monitorData, setMonitorData] = useState<MonitorDataRow[]>([]);
  const [monitorFields, setMonitorFields] = useState<MonitorField[]>([]);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<any>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [itemToEdit, setItemToEdit] = useState<MonitorDataRow | null>(null);
  const [editFormData, setEditFormData] = useState({
    FieldValue: '',
    Note: ''
  });
  
  // Filter state matching Access form
  const [filters, setFilters] = useState({
    fundId: '',
    fieldId: '',
    dateFrom: '',
    dateTo: '',
    hardViolation: null as boolean | null, // tri-state: null (all), true (yes), false (no)
    softViolation: null as boolean | null  // tri-state: null (all), true (yes), false (no)
  });

  useEffect(() => {
    // Only load if not already loaded
    if (!initialLoadComplete) {
      loadMonitorData();
      loadMonitorFields();
      setInitialLoadComplete(true);
    }
  }, []);

  const loadMonitorData = async (applyCurrentFilters = false, forceReload = false) => {
    // Skip if data is already loaded and not forcing reload
    if (!forceReload && !applyCurrentFilters && monitorData.length > 0) {
      console.log('Using cached monitor data - not reloading');
      return;
    }
    
    console.log('Loading monitor data from API...', { forceReload, applyCurrentFilters, cachedLength: monitorData.length });
    
    try {
      setLoading(true);
      setError(null);
      
      const token = await getAccessToken();
      if (!token) {
        setError('No access token available');
        return;
      }

      const params = new URLSearchParams();
      if (applyCurrentFilters) {
        if (filters.fundId) params.append('backstop_id', filters.fundId);
        if (filters.fieldId) params.append('field_id', filters.fieldId);
        // Convert YYYY-MM to YYYY-MM-DD for backend
        if (filters.dateFrom) params.append('start_date', `${filters.dateFrom}-01`);
        if (filters.dateTo) {
          // For end date, use last day of month
          const [year, month] = filters.dateTo.split('-');
          const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
          params.append('end_date', `${filters.dateTo}-${lastDay}`);
        }
      }

      const queryString = params.toString();
      const url = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/monitor-data${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Monitor data loaded with thresholds:', data.length, 'records');
        console.log('Sample record:', data[0]); // Debug: check what fields are returned
        setMonitorData(data);
      } else {
        setError(`Failed to load monitor data: ${response.status}`);
        setMonitorData([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load monitor data');
      setMonitorData([]);
    } finally {
      setLoading(false);
    }
  };

  const loadMonitorFields = async (fundId?: string) => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      // If a fund is selected, load only fields for that fund
      // Otherwise, load all fields
      const url = fundId 
        ? `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/monitor-fields-for-fund/${fundId}`
        : `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/monitor-fields`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMonitorFields(data);
        console.log(`Loaded ${data.length} monitor fields ${fundId ? `for fund ${fundId}` : '(all fields)'}`);
      }
    } catch (err) {
      console.error('Failed to load monitor fields:', err);
    }
  };

  const handleEdit = (row: MonitorDataRow) => {
    setItemToEdit(row);
    setEditFormData({
      FieldValue: row.FieldValue.toString(),
      Note: row.Note || ''
    });
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!itemToEdit) return;
    
    setLoading(true);
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('No access token available');
      
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/monitor-data/${itemToEdit.MonitorDataID}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            BackstopID: itemToEdit.BackstopID,
            FieldID: itemToEdit.FieldID,
            MonitorDate: itemToEdit.MonitorDate.split('T')[0],
            FieldValue: parseFloat(editFormData.FieldValue),
            Note: editFormData.Note
          })
        }
      );
      
      if (!response.ok) throw new Error('Failed to update monitor data');
      
      setSuccess('Monitor data updated successfully');
      setEditDialogOpen(false);
      setItemToEdit(null);
      loadMonitorData(false, true); // Force reload after edit
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    
    setLoading(true);
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('No access token available');
      
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}/api/hedgefund/monitor-data/${itemToDelete.MonitorDataID}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) throw new Error('Failed to delete monitor data');
      
      setSuccess('Monitor data deleted successfully');
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      loadMonitorData(false, true); // Force reload after delete
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setLoading(false);
    }
  };

  // Apply frontend filtering for violations
  const getFilteredData = () => {
    return monitorData.filter(row => {
      // Apply hard violation filter
      if (filters.hardViolation !== null) {
        if (filters.hardViolation && !row.HardViolation) return false;
        if (!filters.hardViolation && row.HardViolation) return false;
      }
      
      // Apply soft violation filter
      if (filters.softViolation !== null) {
        if (filters.softViolation && !row.SoftViolation) return false;
        if (!filters.softViolation && row.SoftViolation) return false;
      }
      
      return true;
    });
  };

  const filteredData = getFilteredData();

  const { sortedData, handleSort, getSortDirection, getSortIndex } = useTableSort({
    data: filteredData,
    initialSort: [{ field: 'MonitorDate', direction: 'desc' }]
  });

  const applyFilters = () => {
    loadMonitorData(true);
  };

  const clearFilters = () => {
    setFilters({
      fundId: '',
      fieldId: '',
      dateFrom: '',
      dateTo: '',
      hardViolation: null,
      softViolation: null
    });
    setTimeout(() => loadMonitorData(false), 100);
  };

  // Format date as mmm-yy (e.g. "Jan-24")
  const formatDateDisplay = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const year = date.getFullYear().toString().slice(-2);
    return `${month}-${year}`;
  };

  // Threshold/value fields: show up to 4dp, trimming trailing zeros, floor of 2dp
  const formatValue = (value: number | null | undefined) => {
    if (value == null) return '-';
    const rounded = Math.round((value + Number.EPSILON) * 10000) / 10000;
    const decimals = rounded.toFixed(4).replace(/0+$/, '').replace(/\.$/, '').split('.')[1]?.length ?? 0;
    return rounded.toFixed(Math.max(2, decimals));
  };

  // Risk level (RI): categorical indicator ID (1-6), show as plain integer, no decimals
  const formatRiskLevel = (value: number | null | undefined) => {
    if (value == null) return '-';
    return value.toFixed(0);
  };

  const handleExportCSV = () => {
    try {
      console.log('Starting CSV export, records:', sortedData.length);
      
      if (sortedData.length === 0) {
        setError('No data to export');
        return;
      }

      let csvContent = 'Monitor Data Export - Browse Data\n';
      csvContent += `Export Date: ${new Date().toLocaleString()}\n`;
      csvContent += `Total Records: ${sortedData.length}\n\n`;
      
      // Add filter settings
      csvContent += 'Applied Filters:\n';
      
      const selectedFund = funds.find(f => f.BackstopID.toString() === filters.fundId);
      const selectedField = monitorFields.find(f => f.FieldID.toString() === filters.fieldId);
      
      if (filters.fundId) {
        csvContent += `Fund: ${selectedFund?.FundName || filters.fundId}\n`;
      }
      if (filters.fieldId) {
        csvContent += `Field: ${selectedField?.FieldName || filters.fieldId}\n`;
      }
      if (filters.dateFrom) {
        csvContent += `Date From: ${filters.dateFrom}\n`;
      }
      if (filters.dateTo) {
        csvContent += `Date To: ${filters.dateTo}\n`;
      }
      if (filters.hardViolation !== null) {
        csvContent += `Hard Violation (LII): ${filters.hardViolation ? 'Yes only' : 'No only'}\n`;
      }
      if (filters.softViolation !== null) {
        csvContent += `Soft Violation (LI): ${filters.softViolation ? 'Yes only' : 'No only'}\n`;
      }
      if (!filters.fundId && !filters.fieldId && !filters.dateFrom && !filters.dateTo && 
          filters.hardViolation === null && filters.softViolation === null) {
        csvContent += 'No filters applied (showing all data)\n';
      }
      
      csvContent += '\n';
      csvContent += 'Date,Fund,Field,L1,L2,U1,U2,Value,LII,LI,Risk Indicator,RI,Note\n';
      
      sortedData.forEach((row, index) => {
        try {
          const date = formatDateDisplay(row.MonitorDate || '');
          const li = row.SoftViolation ? 'Yes' : 'No';
          const lii = row.HardViolation ? 'Yes' : 'No';
          const note = (row.Note || '').replace(/,/g, ';').replace(/"/g, '""');
          
          const l1 = row.SoftLower != null ? formatValue(row.SoftLower) : '';
          const l2 = row.HardLower != null ? formatValue(row.HardLower) : '';
          const u1 = row.SoftUpper != null ? formatValue(row.SoftUpper) : '';
          const u2 = row.HardUpper != null ? formatValue(row.HardUpper) : '';
          const value = row.FieldValue != null ? formatValue(row.FieldValue) : '';
          const ri = row.RiskLevel != null ? row.RiskLevel.toFixed(0) : '';
          
          csvContent += `"${date}","${row.FundName || ''}","${row.FieldName || ''}",`;
          csvContent += `${l1},${l2},${u1},${u2},${value},"${lii}","${li}",`;
          csvContent += `"${row.RiskIndicatorName || ''}",${ri},"${note}"\n`;
        } catch (rowErr) {
          console.error(`Error exporting row ${index}:`, rowErr, row);
        }
      });

      console.log('CSV content prepared, creating blob');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `monitor_data_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log('CSV export completed successfully');
      setSuccess('Data exported to CSV successfully');
    } catch (err) {
      console.error('CSV export error:', err);
      setError(`Export failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const handleExportPDF = () => {
    try {
      console.log('Starting PDF export, records:', sortedData.length);
      
      if (sortedData.length === 0) {
        setError('No data to export');
        return;
      }

      const doc = new jsPDF('landscape', 'pt', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      // Title
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text('Monitor Data Export - Browse Data', pageWidth / 2, 30, { align: 'center' });
      
      // Export info
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`Export Date: ${new Date().toLocaleString()}`, 40, 50);
      doc.text(`Total Records: ${sortedData.length}`, 40, 62);
      
      // Filter settings
      let yPos = 74;
      doc.setFont('helvetica', 'bold');
      doc.text('Applied Filters:', 40, yPos);
      doc.setFont('helvetica', 'normal');
      yPos += 12;
      
      const selectedFund = funds.find(f => f.BackstopID.toString() === filters.fundId);
      const selectedField = monitorFields.find(f => f.FieldID.toString() === filters.fieldId);
      
      if (filters.fundId) {
        doc.text(`• Fund: ${selectedFund?.FundName || filters.fundId}`, 50, yPos);
        yPos += 12;
      }
      if (filters.fieldId) {
        doc.text(`• Field: ${selectedField?.FieldName || filters.fieldId}`, 50, yPos);
        yPos += 12;
      }
      if (filters.dateFrom) {
        doc.text(`• Date From: ${filters.dateFrom}`, 50, yPos);
        yPos += 12;
      }
      if (filters.dateTo) {
        doc.text(`• Date To: ${filters.dateTo}`, 50, yPos);
        yPos += 12;
      }
      if (filters.hardViolation !== null) {
        doc.text(`• Hard Violation (LII): ${filters.hardViolation ? 'Yes only' : 'No only'}`, 50, yPos);
        yPos += 12;
      }
      if (filters.softViolation !== null) {
        doc.text(`• Soft Violation (LI): ${filters.softViolation ? 'Yes only' : 'No only'}`, 50, yPos);
        yPos += 12;
      }
      if (!filters.fundId && !filters.fieldId && !filters.dateFrom && !filters.dateTo && 
          filters.hardViolation === null && filters.softViolation === null) {
        doc.text('• No filters applied (showing all data)', 50, yPos);
        yPos += 12;
      }
      
      yPos += 10;
      
      // Prepare table data
      const tableData = sortedData.map(row => [
        formatDateDisplay(row.MonitorDate),
        row.FundName || '',
        row.FieldName || '',
        formatValue(row.SoftLower),
        formatValue(row.HardLower),
        formatValue(row.SoftUpper),
        formatValue(row.HardUpper),
        formatValue(row.FieldValue),
        row.HardViolation ? 'Yes' : '',
        row.SoftViolation ? 'Yes' : '',
        row.RiskIndicatorName || '-',
        row.RiskLevel != null ? row.RiskLevel.toFixed(0) : '-',
        (row.Note || '-').substring(0, 30) // Truncate long notes
      ]);
      
      // Create table
      autoTable(doc, {
        head: [['Date', 'Fund', 'Field', 'L1', 'L2', 'U1', 'U2', 'Value', 'LII', 'LI', 'Risk Indicator', 'RI', 'Note']],
        body: tableData,
        startY: yPos,
        styles: {
          fontSize: 7,
          cellPadding: 2,
          overflow: 'linebreak',
        },
        headStyles: {
          fillColor: [26, 35, 126], // #1a237e
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 7,
        },
        columnStyles: {
          0: { cellWidth: 45 }, // Date
          1: { cellWidth: 130 }, // Fund
          2: { cellWidth: 130 }, // Field
          3: { cellWidth: 35, halign: 'right' }, // L1
          4: { cellWidth: 35, halign: 'right' }, // L2
          5: { cellWidth: 35, halign: 'right' }, // U1
          6: { cellWidth: 35, halign: 'right' }, // U2
          7: { cellWidth: 40, halign: 'right', fontStyle: 'bold' }, // Value
          8: { cellWidth: 25 }, // LII
          9: { cellWidth: 25 }, // LI
          10: { cellWidth: 70 }, // Risk Indicator
          11: { cellWidth: 30, halign: 'right' }, // RI
          12: { cellWidth: 127 }, // Note
        },
        didDrawPage: (data) => {
          // Footer with page number
          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          const pageNumber = doc.getCurrentPageInfo().pageNumber;
          doc.text(
            `Page ${pageNumber}`,
            pageWidth / 2,
            pageHeight - 20,
            { align: 'center' }
          );
        },
        margin: { top: yPos, left: 40, right: 40, bottom: 30 },
      });
      
      // Save PDF
      doc.save(`monitor_data_${new Date().toISOString().split('T')[0]}.pdf`);
      
      console.log('PDF export completed successfully');
      setSuccess('Data exported to PDF successfully');
    } catch (err) {
      console.error('PDF export error:', err);
      setError(`PDF export failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Filters Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Filters</Typography>
          <ButtonGroup variant="outlined" size="small">
            <Button startIcon={<ExportIcon />} onClick={handleExportCSV}>
              Export CSV
            </Button>
            <Button startIcon={<PdfIcon />} onClick={handleExportPDF}>
              Export PDF
            </Button>
          </ButtonGroup>
        </Box>
        
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter by Fund</InputLabel>
              <Select
                value={filters.fundId}
                onChange={(e) => {
                  const newFundId = e.target.value;
                  setFilters(prev => ({ ...prev, fundId: newFundId, fieldId: '' })); // Reset field when fund changes
                  loadMonitorFields(newFundId || undefined); // Load fields for selected fund
                }}
                label="Filter by Fund"
              >
                <MenuItem value="">All Funds</MenuItem>
                {funds.map(fund => (
                  <MenuItem key={fund.BackstopID} value={fund.BackstopID.toString()}>
                    {fund.FundName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={2.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter by Field</InputLabel>
              <Select
                value={filters.fieldId}
                onChange={(e) => setFilters(prev => ({ ...prev, fieldId: e.target.value }))}
                label="Filter by Field"
              >
                <MenuItem value="">All Fields</MenuItem>
                {monitorFields.map(field => (
                  <MenuItem key={field.FieldID} value={field.FieldID.toString()}>
                    {field.FieldName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={1.5}>
            <TextField
              label="Date From (mmm yy)"
              type="month"
              size="small"
              value={filters.dateFrom}
              onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
              placeholder="YYYY-MM"
            />
          </Grid>
          
          <Grid item xs={12} md={1.5}>
            <TextField
              label="Date To (mmm yy)"
              type="month"
              size="small"
              value={filters.dateTo}
              onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
              placeholder="YYYY-MM"
            />
          </Grid>
          
          <Grid item xs={12} md={1.5}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.hardViolation === true}
                  indeterminate={filters.hardViolation === null}
                  onChange={(e) => {
                    if (filters.hardViolation === null) {
                      setFilters(prev => ({ ...prev, hardViolation: true }));
                    } else if (filters.hardViolation === true) {
                      setFilters(prev => ({ ...prev, hardViolation: false }));
                    } else {
                      setFilters(prev => ({ ...prev, hardViolation: null }));
                    }
                  }}
                  size="small"
                />
              }
              label={<Typography variant="body2">Hard (LII)</Typography>}
            />
          </Grid>
          
          <Grid item xs={12} md={1.5}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.softViolation === true}
                  indeterminate={filters.softViolation === null}
                  onChange={(e) => {
                    if (filters.softViolation === null) {
                      setFilters(prev => ({ ...prev, softViolation: true }));
                    } else if (filters.softViolation === true) {
                      setFilters(prev => ({ ...prev, softViolation: false }));
                    } else {
                      setFilters(prev => ({ ...prev, softViolation: null }));
                    }
                  }}
                  size="small"
                />
              }
              label={<Typography variant="body2">Soft (LI)</Typography>}
            />
          </Grid>
          
          <Grid item xs={12} md={2}>
            <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
              <Button 
                variant="outlined" 
                size="small" 
                startIcon={<FilterIcon />} 
                onClick={applyFilters} 
                sx={{ flex: 1, minWidth: 0 }}
              >
                Apply
              </Button>
              <Button 
                variant="outlined" 
                size="small" 
                onClick={clearFilters}
                sx={{ flex: 1, minWidth: 0 }}
              >
                Clear
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Data Table - Full Width */}
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Monitor Data ({sortedData.length} of {monitorData.length} records)
            {loading && <CircularProgress size={20} sx={{ ml: 2, verticalAlign: 'middle' }} />}
          </Typography>
          <Tooltip title="Refresh Monitor Data">
            <IconButton 
              onClick={() => loadMonitorData(false, true)} 
              disabled={loading}
              size="small"
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <TableContainer>
          <Table size="small" sx={{ '& .MuiTableCell-root': { fontSize: '0.75rem' } }}>
            <TableHead>
              <TableRow>
                <SortableTableHeader field="MonitorDate" label="Date" sortDirection={getSortDirection('MonitorDate')} sortIndex={getSortIndex('MonitorDate')} onSort={handleSort} />
                <SortableTableHeader field="FundName" label="Fund" sortDirection={getSortDirection('FundName')} sortIndex={getSortIndex('FundName')} onSort={handleSort} />
                <SortableTableHeader field="FieldName" label="Field" sortDirection={getSortDirection('FieldName')} sortIndex={getSortIndex('FieldName')} onSort={handleSort} />
                <SortableTableHeader field="SoftLower" label="L1" sortDirection={getSortDirection('SoftLower')} sortIndex={getSortIndex('SoftLower')} onSort={handleSort} align="right" />
                <SortableTableHeader field="HardLower" label="L2" sortDirection={getSortDirection('HardLower')} sortIndex={getSortIndex('HardLower')} onSort={handleSort} align="right" />
                <SortableTableHeader field="SoftUpper" label="U1" sortDirection={getSortDirection('SoftUpper')} sortIndex={getSortIndex('SoftUpper')} onSort={handleSort} align="right" />
                <SortableTableHeader field="HardUpper" label="U2" sortDirection={getSortDirection('HardUpper')} sortIndex={getSortIndex('HardUpper')} onSort={handleSort} align="right" />
                <SortableTableHeader field="FieldValue" label="Value" sortDirection={getSortDirection('FieldValue')} sortIndex={getSortIndex('FieldValue')} onSort={handleSort} align="right" />
                <SortableTableHeader field="HardViolation" label="LII" sortDirection={getSortDirection('HardViolation')} sortIndex={getSortIndex('HardViolation')} onSort={handleSort} />
                <SortableTableHeader field="SoftViolation" label="LI" sortDirection={getSortDirection('SoftViolation')} sortIndex={getSortIndex('SoftViolation')} onSort={handleSort} />
                <SortableTableHeader field="RiskIndicatorName" label="Risk Indicator" sortDirection={getSortDirection('RiskIndicatorName')} sortIndex={getSortIndex('RiskIndicatorName')} onSort={handleSort} />
                <SortableTableHeader field="RiskLevel" label="RI" sortDirection={getSortDirection('RiskLevel')} sortIndex={getSortIndex('RiskLevel')} onSort={handleSort} align="right" />
                <SortableTableHeader field="Note" label="Note" sortDirection={getSortDirection('Note')} sortIndex={getSortIndex('Note')} onSort={handleSort} />
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={14} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No monitor data found. Adjust filters or add data.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
                  <TableRow key={`${row.MonitorDataID}-${index}`} hover>
                    <TableCell>{formatDateDisplay(row.MonitorDate)}</TableCell>
                    <TableCell>{row.FundName}</TableCell>
                    <TableCell>{row.FieldName}</TableCell>
                    <TableCell align="right">{formatValue(row.SoftLower)}</TableCell>
                    <TableCell align="right">{formatValue(row.HardLower)}</TableCell>
                    <TableCell align="right">{formatValue(row.SoftUpper)}</TableCell>
                    <TableCell align="right">{formatValue(row.HardUpper)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatValue(row.FieldValue)}</TableCell>
                    <TableCell>
                      {row.HardViolation && (
                        <Chip label="Yes" size="small" color="error" />
                      )}
                    </TableCell>
                    <TableCell>
                      {row.SoftViolation && (
                        <Chip label="Yes" size="small" color="warning" />
                      )}
                    </TableCell>
                    <TableCell>{row.RiskIndicatorName || '-'}</TableCell>
                    <TableCell align="right">{formatRiskLevel(row.RiskLevel)}</TableCell>
                    <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.Note ? (
                        <Tooltip title={row.Note} arrow placement="top">
                          <span>{row.Note}</span>
                        </Tooltip>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(row)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setItemToDelete(row);
                              setDeleteDialogOpen(true);
                            }}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={sortedData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Monitor Data</DialogTitle>
        <DialogContent>
          {itemToEdit && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Fund:</strong> {itemToEdit.FundName}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Field:</strong> {itemToEdit.FieldName}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                <strong>Date:</strong> {new Date(itemToEdit.MonitorDate).toLocaleDateString()}
              </Typography>
              
              <TextField
                label="Field Value"
                type="number"
                value={editFormData.FieldValue}
                onChange={(e) => setEditFormData(prev => ({ ...prev, FieldValue: e.target.value }))}
                fullWidth
                required
                sx={{ mb: 2 }}
                inputProps={{ step: '0.01' }}
              />
              
              <TextField
                label="Note"
                value={editFormData.Note}
                onChange={(e) => setEditFormData(prev => ({ ...prev, Note: e.target.value }))}
                fullWidth
                multiline
                rows={3}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleEditSave} 
            variant="contained" 
            disabled={loading || !editFormData.FieldValue}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this monitor data record? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" disabled={loading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BrowseEditView;
